import { createContext, FC, PropsWithChildren, useContext } from 'react';

import { ContentParametersBulkUpdateMode, ContentParametersMapping } from 'data/generated/graphql';
import { MappingActionDispatchFn } from './useMappingState';

type MappingContextType = ContentParametersMapping & {
  dispatch: MappingActionDispatchFn;
  bulkUpdateMode?: ContentParametersBulkUpdateMode;
  bulkUpdateArtifactNames?: string[];
  currentHistoryIndex: number | null;
  actions: {
    history: {
      handlePrevious: () => void;
      handleNext: () => void;
    };
  };
  artifactsPerTreatment?: string[] | undefined;
};

export const MappingContext = createContext<MappingContextType | null>(null);

export const useMappingContext = () => {
  const context = useContext(MappingContext);
  if (!context) {
    throw new Error('useMappingContext must be used within a MappingContext.Provider');
  }
  return context;
};

interface Props {
  mapping: ContentParametersMapping;
  dispatch: MappingActionDispatchFn;
  bulkUpdateMode?: ContentParametersBulkUpdateMode;
  bulkUpdateArtifactNames?: string[];
  originalMapping: ContentParametersMapping;
  historyIndex?: number | null;
  artifactsPerTreatment?: string[] | undefined;
}

export const MappingProvider: FC<PropsWithChildren<Props>> = ({
  mapping,
  children,
  dispatch,
  bulkUpdateMode,
  bulkUpdateArtifactNames,
  originalMapping,
  historyIndex,
  artifactsPerTreatment
}) => {
  const { history } = mapping;

  const hasHistory = Array.isArray(history) && history.length > 0;

  const handlePrevious = () => {
    // No history item is selected
    if (typeof historyIndex !== 'number' && hasHistory) {
      const historyItem = history[0];

      dispatch({
        type: 'SET_HISTORY_INDEX',
        payload: { treatmentType: mapping.treatmentType, index: 0 }
      });

      dispatch({
        type: 'UPDATE_PARAMETERS',
        payload: {
          treatmentType: mapping.treatmentType,
          value: historyItem.parameters
        }
      });

      dispatch({
        type: 'UPDATE_COMMENT',
        payload: {
          treatmentType: mapping.treatmentType,
          value: historyItem.comment ?? ''
        }
      });

      return;
    }

    if (typeof historyIndex === 'number' && hasHistory) {
      const nextIndex = historyIndex + 1;
      if (nextIndex < history.length) {
        const historyItem = history[nextIndex];

        dispatch({
          type: 'SET_HISTORY_INDEX',
          payload: { treatmentType: mapping.treatmentType, index: nextIndex }
        });

        dispatch({
          type: 'UPDATE_PARAMETERS',
          payload: {
            treatmentType: mapping.treatmentType,
            value: historyItem.parameters
          }
        });

        dispatch({
          type: 'UPDATE_COMMENT',
          payload: {
            treatmentType: mapping.treatmentType,
            value: historyItem.comment ?? ''
          }
        });
      }
    }
  };

  const handleNext = () => {
    if (typeof historyIndex === 'number' && hasHistory) {
      const prevIndex = historyIndex - 1;
      if (prevIndex >= 0) {
        const historyItem = history[prevIndex];

        dispatch({
          type: 'SET_HISTORY_INDEX',
          payload: { treatmentType: mapping.treatmentType, index: prevIndex }
        });

        dispatch({
          type: 'UPDATE_PARAMETERS',
          payload: {
            treatmentType: mapping.treatmentType,
            value: historyItem.parameters
          }
        });

        dispatch({
          type: 'UPDATE_COMMENT',
          payload: {
            treatmentType: mapping.treatmentType,
            value: historyItem.comment ?? ''
          }
        });
      } else {
        // Reset to current mapping
        dispatch({
          type: 'SET_HISTORY_INDEX',
          payload: { treatmentType: mapping.treatmentType, index: null }
        });

        dispatch({
          type: 'UPDATE_PARAMETERS',
          payload: {
            treatmentType: mapping.treatmentType,
            value: originalMapping.parameters
          }
        });

        dispatch({
          type: 'UPDATE_COMMENT',
          payload: {
            treatmentType: mapping.treatmentType,
            value: originalMapping.comment ?? ''
          }
        });
      }
    }
  };

  return (
    <MappingContext.Provider
      value={{
        ...mapping,
        dispatch,
        bulkUpdateMode,
        bulkUpdateArtifactNames,
        currentHistoryIndex: historyIndex ?? null,
        actions: {
          history: {
            handlePrevious,
            handleNext
          }
        },
        artifactsPerTreatment
      }}
    >
      {children}
    </MappingContext.Provider>
  );
};
